import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, DollarSign, TrendingUp, RefreshCw, Search, X, Phone, Mail, MapPin, Calendar, CreditCard, FileText } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Separator } from '../../components/ui/separator';
import StatusBadge from '../../components/common/StatusBadge';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

export default function AdminDashboard() {
  const { accessToken } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [confirmedCustomers, setConfirmedCustomers] = useState([]);
  const [pendingCustomers, setPendingCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('customers');
  
  // Customer detail sheet state
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetail, setCustomerDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      
      const [metricsRes, confirmedRes, pendingRes, ordersRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, { headers }),
        axios.get(`${API}/admin/customers/confirmed`, { headers }),
        axios.get(`${API}/admin/customers/pending`, { headers }),
        axios.get(`${API}/admin/orders`, { headers })
      ]);
      
      setMetrics(metricsRes.data);
      setConfirmedCustomers(confirmedRes.data);
      setPendingCustomers(pendingRes.data);
      setOrders(ordersRes.data.orders || []);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerDetail = async (customerId) => {
    setLoadingDetail(true);
    try {
      const response = await axios.get(`${API}/admin/customers/${customerId}`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      setCustomerDetail(response.data);
    } catch (error) {
      console.error('Failed to fetch customer details:', error);
      toast.error('Failed to load customer details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleCustomerClick = (customer) => {
    setSelectedCustomer(customer);
    setSheetOpen(true);
    fetchCustomerDetail(customer.id);
  };

  const handleRefund = async (orderId, orderNumber) => {
    if (!window.confirm(`Are you sure you want to refund order ${orderNumber}?`)) {
      return;
    }
    
    try {
      await axios.post(
        `${API}/admin/orders/${orderId}/refund`,
        { reason: 'Admin initiated refund' },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      
      toast.success('Refund processed successfully');
      fetchData(); // Refresh data
      // Refresh customer detail if open
      if (customerDetail) {
        fetchCustomerDetail(customerDetail.user.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process refund');
    }
  };

  const filterCustomers = (customers) => {
    if (!searchTerm) return customers;
    const term = searchTerm.toLowerCase();
    return customers.filter(c => 
      c.email.toLowerCase().includes(term) ||
      c.firstName.toLowerCase().includes(term) ||
      c.lastName.toLowerCase().includes(term)
    );
  };

  const filterOrders = (ordersList) => {
    if (!searchTerm) return ordersList;
    const term = searchTerm.toLowerCase();
    return ordersList.filter(o => 
      o.orderNumber?.toLowerCase().includes(term) ||
      o.userEmail?.toLowerCase().includes(term)
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateShort = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-pulse text-[#00B4B4]">Loading admin dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7FBFD]" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-white border-b border-slate-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-between items-center">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h1 className="text-2xl sm:text-3xl font-semibold text-[#1A365D]">
                Admin Dashboard
              </h1>
              <p className="text-slate-600 mt-1">
                Manage customers, orders, and view metrics
              </p>
            </motion.div>
            <Button 
              variant="outline" 
              onClick={fetchData}
              className="flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Metrics */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Confirmed Customers</p>
                    <p className="text-3xl font-bold text-[#1A365D] mt-1">
                      {metrics?.confirmedCustomers || 0}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                    <Users className="h-6 w-6 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Pending Customers</p>
                    <p className="text-3xl font-bold text-[#1A365D] mt-1">
                      {metrics?.pendingCustomers || 0}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                    <Users className="h-6 w-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Total Revenue</p>
                    <p className="text-3xl font-bold text-[#1A365D] mt-1">
                      ${(metrics?.totalRevenue || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-[#00B4B4]/10 rounded-full flex items-center justify-center">
                    <DollarSign className="h-6 w-6 text-[#00B4B4]" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Recent Orders (7d)</p>
                    <p className="text-3xl font-bold text-[#1A365D] mt-1">
                      {metrics?.recentOrders || 0}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-12">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <TabsList data-testid="admin-tab">
              <TabsTrigger value="customers">Customers</TabsTrigger>
              <TabsTrigger value="orders">Orders</TabsTrigger>
            </TabsList>
            
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="admin-search-input"
              />
            </div>
          </div>

          <TabsContent value="customers">
            <Card data-testid="admin-table">
              <CardHeader>
                <CardTitle>Confirmed Customers ({confirmedCustomers.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-100">
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Customer</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Email</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Edition</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">POA</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Registered</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filterCustomers(confirmedCustomers).map((customer) => (
                        <tr 
                          key={customer.id} 
                          className="border-b border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer"
                          onClick={() => handleCustomerClick(customer)}
                          data-testid="admin-table-row"
                        >
                          <td className="py-3 px-4">
                            <span className="font-medium text-[#1A365D]">
                              {customer.firstName} {customer.lastName}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-600">
                            {customer.email}
                          </td>
                          <td className="py-3 px-4 text-sm">
                            <span className="capitalize">{customer.productEdition || '-'}</span>
                          </td>
                          <td className="py-3 px-4 text-sm">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${customer.poaSigned ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                              {customer.poaSigned ? 'Signed' : 'Pending'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-500">
                            {formatDate(customer.createdAt)}
                          </td>
                          <td className="py-3 px-4">
                            <StatusBadge status={customer.paymentStatus} />
                          </td>
                          <td className="py-3 px-4">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-[#00B4B4] hover:text-[#00B4B4] hover:bg-[#00B4B4]/10"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCustomerClick(customer);
                              }}
                              data-testid="view-customer-btn"
                            >
                              View Details
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filterCustomers(confirmedCustomers).length === 0 && (
                    <p className="text-center py-8 text-slate-500">No confirmed customers found</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {pendingCustomers.length > 0 && (
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle>Pending Customers ({pendingCustomers.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-100">
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Customer</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Email</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Registered</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Status</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filterCustomers(pendingCustomers).map((customer) => (
                          <tr 
                            key={customer.id} 
                            className="border-b border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer"
                            onClick={() => handleCustomerClick(customer)}
                          >
                            <td className="py-3 px-4">
                              <span className="font-medium text-[#1A365D]">
                                {customer.firstName} {customer.lastName}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-sm text-slate-600">
                              {customer.email}
                            </td>
                            <td className="py-3 px-4 text-sm text-slate-500">
                              {formatDate(customer.createdAt)}
                            </td>
                            <td className="py-3 px-4">
                              <StatusBadge status={customer.paymentStatus} />
                            </td>
                            <td className="py-3 px-4">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-[#00B4B4] hover:text-[#00B4B4] hover:bg-[#00B4B4]/10"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCustomerClick(customer);
                                }}
                              >
                                View Details
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="orders">
            <Card data-testid="admin-table">
              <CardHeader>
                <CardTitle>All Orders ({orders.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-100">
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Order #</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Customer</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Amount</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Date</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filterOrders(orders).map((order) => (
                        <tr 
                          key={order.id} 
                          className="border-b border-slate-50 hover:bg-slate-50 transition-colors"
                          data-testid="admin-table-row"
                        >
                          <td className="py-3 px-4">
                            <span className="font-mono text-sm font-medium text-[#1A365D]">
                              {order.orderNumber}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-600">
                            {order.userEmail}
                          </td>
                          <td className="py-3 px-4 text-sm font-medium">
                            ${(order.total / 100).toFixed(2)}
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-500">
                            {formatDate(order.createdAt)}
                          </td>
                          <td className="py-3 px-4">
                            <StatusBadge status={order.status} />
                          </td>
                          <td className="py-3 px-4">
                            {order.status === 'completed' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleRefund(order.id, order.orderNumber)}
                              >
                                Refund
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filterOrders(orders).length === 0 && (
                    <p className="text-center py-8 text-slate-500">No orders found</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Customer Detail Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="customer-detail-sheet">
          <SheetHeader>
            <SheetTitle className="text-[#1A365D]">Customer Details</SheetTitle>
          </SheetHeader>
          
          {loadingDetail ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-pulse text-[#00B4B4]">Loading...</div>
            </div>
          ) : customerDetail ? (
            <div className="mt-6 space-y-6">
              {/* Basic Info */}
              <div>
                <h3 className="text-sm font-medium text-slate-500 mb-3">Personal Information</h3>
                <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Name</span>
                    <span className="font-medium text-[#1A365D]" data-testid="customer-name">
                      {customerDetail.user.firstName} {customerDetail.user.lastName}
                    </span>
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600 flex items-center gap-2">
                      <Mail className="h-4 w-4" /> Email
                    </span>
                    <span className="font-medium text-[#1A365D]" data-testid="customer-email">
                      {customerDetail.user.email}
                    </span>
                  </div>
                  {customerDetail.user.phone && (
                    <>
                      <Separator />
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-600 flex items-center gap-2">
                          <Phone className="h-4 w-4" /> Phone
                        </span>
                        <span className="font-medium text-[#1A365D]" data-testid="customer-phone">
                          {customerDetail.user.phone}
                        </span>
                      </div>
                    </>
                  )}
                  {customerDetail.user.address && (
                    <>
                      <Separator />
                      <div className="flex items-start justify-between">
                        <span className="text-sm text-slate-600 flex items-center gap-2">
                          <MapPin className="h-4 w-4" /> Address
                        </span>
                        <span className="font-medium text-[#1A365D] text-right text-sm" data-testid="customer-address">
                          {customerDetail.user.address.street && <div>{customerDetail.user.address.street}</div>}
                          {customerDetail.user.address.city && customerDetail.user.address.state && (
                            <div>{customerDetail.user.address.city}, {customerDetail.user.address.state} {customerDetail.user.address.zipCode}</div>
                          )}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Account Status */}
              <div>
                <h3 className="text-sm font-medium text-slate-500 mb-3">Account Status</h3>
                <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Payment Status</span>
                    <StatusBadge status={customerDetail.user.paymentStatus} />
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Email Verified</span>
                    <span className={`text-sm font-medium ${customerDetail.user.emailVerified ? 'text-emerald-600' : 'text-amber-600'}`}>
                      {customerDetail.user.emailVerified ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">POA Signed</span>
                    <span className={`text-sm font-medium ${customerDetail.user.poaSigned ? 'text-emerald-600' : 'text-amber-600'}`} data-testid="customer-poa-status">
                      {customerDetail.user.poaSigned ? 'Yes' : 'No'}
                    </span>
                  </div>
                  {customerDetail.user.poaSignedAt && (
                    <>
                      <Separator />
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-600">POA Signed Date</span>
                        <span className="text-sm text-[#1A365D]">
                          {formatDateShort(customerDetail.user.poaSignedAt)}
                        </span>
                      </div>
                    </>
                  )}
                  <Separator />
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Product Edition</span>
                    <span className="font-medium text-[#1A365D] capitalize">
                      {customerDetail.user.productEdition || 'None'}
                    </span>
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600 flex items-center gap-2">
                      <Calendar className="h-4 w-4" /> Registered
                    </span>
                    <span className="text-sm text-[#1A365D]">
                      {formatDateShort(customerDetail.user.createdAt)}
                    </span>
                  </div>
                  {customerDetail.user.lastLoginAt && (
                    <>
                      <Separator />
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-600">Last Login</span>
                        <span className="text-sm text-[#1A365D]">
                          {formatDate(customerDetail.user.lastLoginAt)}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Entitlements */}
              {customerDetail.user.entitlements && customerDetail.user.entitlements.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-slate-500 mb-3">Entitlements</h3>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex flex-wrap gap-2">
                      {customerDetail.user.entitlements.map((entitlement, idx) => (
                        <span 
                          key={idx}
                          className="px-3 py-1 bg-[#00B4B4]/10 text-[#00B4B4] text-sm rounded-full"
                        >
                          {entitlement.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Stripe Info */}
              {customerDetail.user.stripeCustomerId && (
                <div>
                  <h3 className="text-sm font-medium text-slate-500 mb-3">Stripe Information</h3>
                  <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600 flex items-center gap-2">
                        <CreditCard className="h-4 w-4" /> Customer ID
                      </span>
                      <span className="font-mono text-xs text-[#1A365D]">
                        {customerDetail.user.stripeCustomerId}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Orders */}
              <div>
                <h3 className="text-sm font-medium text-slate-500 mb-3 flex items-center gap-2">
                  <FileText className="h-4 w-4" /> Order History ({customerDetail.orders?.length || 0})
                </h3>
                {customerDetail.orders && customerDetail.orders.length > 0 ? (
                  <div className="space-y-3">
                    {customerDetail.orders.map((order) => (
                      <div key={order.id} className="bg-slate-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-mono text-sm font-medium text-[#1A365D]">
                            {order.orderNumber}
                          </span>
                          <StatusBadge status={order.status} />
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-600">
                            {formatDateShort(order.createdAt)}
                          </span>
                          <span className="font-medium text-[#1A365D]">
                            ${(order.total / 100).toFixed(2)}
                          </span>
                        </div>
                        {order.status === 'completed' && (
                          <div className="mt-3 pt-3 border-t border-slate-200">
                            <Button
                              variant="outline"
                              size="sm"
                              className="w-full text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => handleRefund(order.id, order.orderNumber)}
                            >
                              Process Refund
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-slate-50 rounded-lg p-4 text-center text-slate-500">
                    No orders yet
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-12 text-slate-500">
              No customer selected
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
